import React, { useRef, useCallback } from 'react';

// 2026 Flying Star Chart Data
// Based on the Lo Shu magic square with annual flying star positions
const FLYING_STARS_2026 = {
  // Position: [row, col] in the 3x3 grid (0-indexed)
  // Grid layout: South at top, North at bottom (traditional Feng Shui orientation)
  SE: { number: 9, star: '右弼星', pinyin: 'You Bi', english: 'You Bi Star', meaning: 'Indirect Wealth', chinese: '偏財', color: '#9b59b6' },
  S:  { number: 5, star: '廉貞星', pinyin: 'Lian Zhen', english: 'Lian Zhen Star', meaning: 'Misfortune & Obstacles', chinese: '五黃', color: '#f1c40f', isBad: true },
  SW: { number: 7, star: '破軍星', pinyin: 'Po Jun', english: 'Po Jun Star', meaning: 'Robbery & Violence', chinese: '七赤', color: '#e74c3c', isBad: true },
  E:  { number: 8, star: '左輔星', pinyin: 'Zuo Fu', english: 'Zuo Fu Star', meaning: 'Direct Wealth', chinese: '正財', color: '#f39c12' },
  CENTER: { number: 1, star: '貪狼星', pinyin: 'Tan Lang', english: 'Tan Lang Star', meaning: 'Literati Benefactor', chinese: '文貴人', color: '#3498db' },
  W:  { number: 3, star: '祿存星', pinyin: 'Lu Cun', english: 'Lu Cun Star', meaning: 'Conflict & Disputes', chinese: '三碧', color: '#27ae60', isBad: true },
  NE: { number: 4, star: '文曲星', pinyin: 'Wen Qu', english: 'Wen Qu Star', meaning: 'Academic & Romance', chinese: '四綠', color: '#2ecc71' },
  N:  { number: 6, star: '武曲星', pinyin: 'Wu Qu', english: 'Wu Qu Star', meaning: 'Martial Benefactor', chinese: '武貴人', color: '#9b59b6' },
  NW: { number: 2, star: '巨門星', pinyin: 'Ju Men', english: 'Ju Men Star', meaning: 'Illness & Instability', chinese: '二黑', color: '#34495e', isBad: true },
};

// Grid positions for the 3x3 Flying Star chart
// Compass orientation: North at TOP, South at BOTTOM
const STAR_GRID = [
  ['NW', 'N', 'NE'],    // Top row (North side) - 2, 6, 4
  ['W', 'CENTER', 'E'], // Middle row - 3, 1, 8
  ['SW', 'S', 'SE'],    // Bottom row (South side) - 7, 5, 9
];

// 24 Mountains data - accurate degree ranges based on Joey Yap reference
// Each mountain spans 15 degrees
// Format: startDegree - endDegree (center degree)
const MOUNTAINS_DATA = [
  // North (北) - 337.6° to 22.5°
  { name: '壬', pinyin: 'Ren', degrees: 345, startDeg: 337.6, endDeg: 352.5, direction: 'N', subDir: 'N1' },
  { name: '子', pinyin: 'Zi', degrees: 0, startDeg: 352.6, endDeg: 7.5, direction: 'N', subDir: 'N2', zodiac: 'Rat', zodiacCn: '鼠', hasAnimal: true },
  { name: '癸', pinyin: 'Gui', degrees: 15, startDeg: 7.6, endDeg: 22.5, direction: 'N', subDir: 'N3' },
  
  // Northeast (東北) - 22.6° to 67.5°
  { name: '丑', pinyin: 'Chou', degrees: 30, startDeg: 22.6, endDeg: 37.5, direction: 'NE', subDir: 'NE1', zodiac: 'Ox', zodiacCn: '牛', hasAnimal: true },
  { name: '艮', pinyin: 'Gen', degrees: 45, startDeg: 37.6, endDeg: 52.5, direction: 'NE', subDir: 'NE2', trigram: true },
  { name: '寅', pinyin: 'Yin', degrees: 60, startDeg: 52.6, endDeg: 67.5, direction: 'NE', subDir: 'NE3', zodiac: 'Tiger', zodiacCn: '虎', hasAnimal: true },
  
  // East (東) - 67.6° to 112.5°
  { name: '甲', pinyin: 'Jia', degrees: 75, startDeg: 67.6, endDeg: 82.5, direction: 'E', subDir: 'E1' },
  { name: '卯', pinyin: 'Mao', degrees: 90, startDeg: 82.6, endDeg: 97.5, direction: 'E', subDir: 'E2', zodiac: 'Rabbit', zodiacCn: '兔', hasAnimal: true },
  { name: '乙', pinyin: 'Yi', degrees: 105, startDeg: 97.6, endDeg: 112.5, direction: 'E', subDir: 'E3' },
  
  // Southeast (東南) - 112.6° to 157.5°
  { name: '辰', pinyin: 'Chen', degrees: 120, startDeg: 112.6, endDeg: 127.5, direction: 'SE', subDir: 'SE1', zodiac: 'Dragon', zodiacCn: '龍', hasAnimal: true },
  { name: '巽', pinyin: 'Xun', degrees: 135, startDeg: 127.6, endDeg: 142.5, direction: 'SE', subDir: 'SE2', trigram: true },
  { name: '巳', pinyin: 'Si', degrees: 150, startDeg: 142.6, endDeg: 157.5, direction: 'SE', subDir: 'SE3', zodiac: 'Snake', zodiacCn: '蛇', hasAnimal: true },
  
  // South (南) - 157.6° to 202.5°
  { name: '丙', pinyin: 'Bing', degrees: 165, startDeg: 157.6, endDeg: 172.5, direction: 'S', subDir: 'S1' },
  { name: '午', pinyin: 'Wu', degrees: 180, startDeg: 172.6, endDeg: 187.5, direction: 'S', subDir: 'S2', zodiac: 'Horse', zodiacCn: '馬', hasAnimal: true },
  { name: '丁', pinyin: 'Ding', degrees: 195, startDeg: 187.6, endDeg: 202.5, direction: 'S', subDir: 'S3' },
  
  // Southwest (西南) - 202.6° to 247.5°
  { name: '未', pinyin: 'Wei', degrees: 210, startDeg: 202.6, endDeg: 217.5, direction: 'SW', subDir: 'SW1', zodiac: 'Goat', zodiacCn: '羊', hasAnimal: true },
  { name: '坤', pinyin: 'Kun', degrees: 225, startDeg: 217.6, endDeg: 232.5, direction: 'SW', subDir: 'SW2', trigram: true },
  { name: '申', pinyin: 'Shen', degrees: 240, startDeg: 232.6, endDeg: 247.5, direction: 'SW', subDir: 'SW3', zodiac: 'Monkey', zodiacCn: '猴', hasAnimal: true },
  
  // West (西) - 247.6° to 292.5°
  { name: '庚', pinyin: 'Geng', degrees: 255, startDeg: 247.6, endDeg: 262.5, direction: 'W', subDir: 'W1' },
  { name: '酉', pinyin: 'You', degrees: 270, startDeg: 262.6, endDeg: 277.5, direction: 'W', subDir: 'W2', zodiac: 'Rooster', zodiacCn: '雞', hasAnimal: true },
  { name: '辛', pinyin: 'Xin', degrees: 285, startDeg: 277.6, endDeg: 292.5, direction: 'W', subDir: 'W3' },
  
  // Northwest (西北) - 292.6° to 337.5°
  { name: '戌', pinyin: 'Xu', degrees: 300, startDeg: 292.6, endDeg: 307.5, direction: 'NW', subDir: 'NW1', zodiac: 'Dog', zodiacCn: '狗', hasAnimal: true },
  { name: '乾', pinyin: 'Qian', degrees: 315, startDeg: 307.6, endDeg: 322.5, direction: 'NW', subDir: 'NW2', trigram: true },
  { name: '亥', pinyin: 'Hai', degrees: 330, startDeg: 322.6, endDeg: 337.5, direction: 'NW', subDir: 'NW3', zodiac: 'Pig', zodiacCn: '豬', hasAnimal: true },
];

const DIRECTIONS = [
  { name: 'NORTH', chinese: '北', short: 'N', degrees: 0, startOffset: '12.5%' },
  { name: 'NORTHEAST', chinese: '東北', short: 'NE', degrees: 45, startOffset: '25%' },
  { name: 'EAST', chinese: '東', short: 'E', degrees: 90, startOffset: '37.5%' },
  { name: 'SOUTHEAST', chinese: '東南', short: 'SE', degrees: 135, startOffset: '50%' },
  { name: 'SOUTH', chinese: '南', short: 'S', degrees: 180, startOffset: '62.5%' },
  { name: 'SOUTHWEST', chinese: '西南', short: 'SW', degrees: 225, startOffset: '75%' },
  { name: 'WEST', chinese: '西', short: 'W', degrees: 270, startOffset: '87.5%' },
  { name: 'NORTHWEST', chinese: '西北', short: 'NW', degrees: 315, startOffset: '0%' },
];

// Zodiac emoji for reliable rendering
const ZODIAC_EMOJI = {
  Rat: '🐀',
  Ox: '🐂',
  Tiger: '🐅',
  Rabbit: '🐇',
  Dragon: '🐉',
  Snake: '🐍',
  Horse: '🐎',
  Goat: '🐐',
  Monkey: '🐒',
  Rooster: '🐓',
  Dog: '🐕',
  Pig: '🐖',
};

// Animal image URLs - all 12 zodiac animals
const ANIMAL_IMAGES = {
  Rat: 'https://static.prod-images.emergentagent.com/jobs/04d0f86b-285d-4b05-a616-8206b940abca/images/00e1ebb8d31b08f92b75a2687036f103dbad524e550678a4ceeae93ceabab1ac.png',
  Ox: 'https://static.prod-images.emergentagent.com/jobs/04d0f86b-285d-4b05-a616-8206b940abca/images/4609338ef14ae37f6bef57007984bf986fa1bc13a47af2d79b109801c2f5e041.png',
  Tiger: 'https://static.prod-images.emergentagent.com/jobs/04d0f86b-285d-4b05-a616-8206b940abca/images/c398b90142fba113739adc63d04e196fa164c80ad23b56889ba76c0f3b179ff7.png',
  Rabbit: 'https://static.prod-images.emergentagent.com/jobs/04d0f86b-285d-4b05-a616-8206b940abca/images/d8dbdf8884e98c18280fbcdbc98952f651171c582e6a2bd7cb6c599fd9b51d91.png',
  Dragon: 'https://static.prod-images.emergentagent.com/jobs/04d0f86b-285d-4b05-a616-8206b940abca/images/a190303e69846f5ffd07290dff54a7edb505a38e3f38bf089bd3a02ba1c5212e.png',
  Snake: 'https://static.prod-images.emergentagent.com/jobs/04d0f86b-285d-4b05-a616-8206b940abca/images/ba8cc14da24e065184191f2abebc44e5952397a24f56eea10fea0bcba73597d4.png',
  Horse: 'https://static.prod-images.emergentagent.com/jobs/04d0f86b-285d-4b05-a616-8206b940abca/images/d64185282e22b7cca250460d33dce97b0820a69e64b9b716501f3dd4c339b227.png',
  Goat: 'https://static.prod-images.emergentagent.com/jobs/04d0f86b-285d-4b05-a616-8206b940abca/images/d2b797ebf3b794452cf41c3d75c28385f5c91727d587ca77ffce56d4cf82a49b.png',
  Monkey: 'https://static.prod-images.emergentagent.com/jobs/04d0f86b-285d-4b05-a616-8206b940abca/images/d5b294b328d280eb0a96f65a79e3ddeeee9cb2ccbf2b9879b7f2837b2cd7063d.png',
  Rooster: 'https://static.prod-images.emergentagent.com/jobs/04d0f86b-285d-4b05-a616-8206b940abca/images/d681251ce8d1aeb60187fa0171a9176ea10f1f9fcaee9d02d2015efe95f1b3d9.png',
  Dog: 'https://static.prod-images.emergentagent.com/jobs/04d0f86b-285d-4b05-a616-8206b940abca/images/75b6b23dbdf366a1a639e309c676c520b1a26f46dd13027989efca334a8c3d9d.png',
  Pig: 'https://static.prod-images.emergentagent.com/jobs/04d0f86b-285d-4b05-a616-8206b940abca/images/09580582075caf1f967d8e3393791094e31e4f4f792c858d11f559f6f19b51fb.png',
};

// Animal icon component - uses images if available, fallback to SVG
const AnimalIcon = ({ animal, size = 24, color = "#C41E3A" }) => {
  // If we have an image URL, use it
  if (ANIMAL_IMAGES[animal]) {
    return (
      <image
        href={ANIMAL_IMAGES[animal]}
        width={size}
        height={size}
        preserveAspectRatio="xMidYMid meet"
      />
    );
  }
  
  // Fallback SVG icons
  const icons = {
    Rat: <g><ellipse cx="12" cy="14" rx="6" ry="5" fill={color}/><circle cx="8" cy="8" r="3" fill={color}/><circle cx="16" cy="8" r="3" fill={color}/></g>,
    Ox: <g><ellipse cx="12" cy="14" rx="7" ry="6" fill={color}/><path d="M5 8 L3 4 M19 8 L21 4" stroke={color} strokeWidth="2" strokeLinecap="round"/></g>,
    Tiger: <g><ellipse cx="12" cy="13" rx="7" ry="6" fill={color}/><path d="M6 7 L4 3 M18 7 L20 3" stroke={color} strokeWidth="2" strokeLinecap="round"/></g>,
    Rabbit: <g><ellipse cx="12" cy="15" rx="5" ry="5" fill={color}/><ellipse cx="8" cy="6" rx="2" ry="6" fill={color}/><ellipse cx="16" cy="6" rx="2" ry="6" fill={color}/></g>,
    Dragon: <g><path d="M6 12 Q12 4 18 12 Q12 20 6 12" fill={color}/><path d="M4 8 L2 5 M20 8 L22 5" stroke={color} strokeWidth="2"/></g>,
    Snake: <g><path d="M8 18 Q4 14 8 10 Q12 6 16 10 Q20 14 16 18" stroke={color} strokeWidth="4" fill="none" strokeLinecap="round"/></g>,
    Horse: <g><ellipse cx="12" cy="14" rx="6" ry="5" fill={color}/><ellipse cx="12" cy="7" rx="3" ry="4" fill={color}/></g>,
    Goat: <g><ellipse cx="12" cy="14" rx="6" ry="5" fill={color}/><path d="M7 6 L5 2 M17 6 L19 2" stroke={color} strokeWidth="2" strokeLinecap="round"/></g>,
    Monkey: <g><circle cx="12" cy="12" r="7" fill={color}/><circle cx="12" cy="12" r="4" fill="#FFE4C4"/></g>,
    Rooster: <g><ellipse cx="12" cy="14" rx="5" ry="6" fill={color}/><path d="M12 4 L10 8 L14 8 Z" fill={color}/></g>,
    Dog: <g><ellipse cx="12" cy="14" rx="6" ry="5" fill={color}/><ellipse cx="12" cy="9" rx="5" ry="4" fill={color}/></g>,
    Pig: <g><ellipse cx="12" cy="13" rx="7" ry="6" fill={color}/><ellipse cx="12" cy="15" rx="3" ry="2" fill="#FFB6C1"/></g>,
  };
  
  return <svg width={size} height={size} viewBox="0 0 24 24">{icons[animal] || <circle cx="12" cy="12" r="10" fill={color}/>}</svg>;
};

export const LuoPanCompass = ({ 
  rotation = 0, 
  opacity = 1, 
  size = 500,
  onRotationChange,
  isDragging = false,
  onDragStart,
  onDragEnd,
  onMoveStart,
  isLocked = false,
  showFlyingStar = false,
  flyingStarScale = 1,
  highlightedDirections = [],
  showCompassOnly = true, // When false, only show torch light
  kuaAnalysisEnabled = false,
  kuaDirections = null,
}) => {
  const compassRef = useRef(null);
  const dragStartAngle = useRef(0);
  const initialRotation = useRef(0);

  const center = size / 2;
  const outerRadius = size * 0.36;

  const getAngle = useCallback((clientX, clientY) => {
    if (!compassRef.current) return 0;
    const rect = compassRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    return Math.atan2(clientY - centerY, clientX - centerX) * (180 / Math.PI) + 90;
  }, []);

  const getDistanceFromCenter = useCallback((clientX, clientY) => {
    if (!compassRef.current) return 0;
    const rect = compassRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    return Math.sqrt(Math.pow(clientX - centerX, 2) + Math.pow(clientY - centerY, 2));
  }, []);

  const handleMouseDown = useCallback((e) => {
    // If locked, don't allow any interaction
    if (isLocked) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    const distance = getDistanceFromCenter(e.clientX, e.clientY);
    if (distance < size * 0.25) {
      onMoveStart?.(e);
    } else {
      dragStartAngle.current = getAngle(e.clientX, e.clientY);
      initialRotation.current = rotation;
      onDragStart?.();

      const handleMouseMove = (moveEvent) => {
        const currentAngle = getAngle(moveEvent.clientX, moveEvent.clientY);
        let newRotation = (initialRotation.current + currentAngle - dragStartAngle.current) % 360;
        if (newRotation < 0) newRotation += 360;
        onRotationChange?.(newRotation);
      };

      const handleMouseUp = () => {
        onDragEnd?.();
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };

      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
  }, [rotation, getAngle, getDistanceFromCenter, size, onRotationChange, onDragStart, onDragEnd, onMoveStart, isLocked]);

  const drawSector = (startAngle, endAngle, innerR, outerR) => {
    const startRad = (startAngle - 90) * Math.PI / 180;
    const endRad = (endAngle - 90) * Math.PI / 180;
    const x1 = center + innerR * Math.cos(startRad);
    const y1 = center + innerR * Math.sin(startRad);
    const x2 = center + outerR * Math.cos(startRad);
    const y2 = center + outerR * Math.sin(startRad);
    const x3 = center + outerR * Math.cos(endRad);
    const y3 = center + outerR * Math.sin(endRad);
    const x4 = center + innerR * Math.cos(endRad);
    const y4 = center + innerR * Math.sin(endRad);
    return `M ${x1} ${y1} L ${x2} ${y2} A ${outerR} ${outerR} 0 0 1 ${x3} ${y3} L ${x4} ${y4} A ${innerR} ${innerR} 0 0 0 ${x1} ${y1}`;
  };

  const getTextPosition = (angle, radius) => {
    const rad = (angle - 90) * Math.PI / 180;
    return { x: center + radius * Math.cos(rad), y: center + radius * Math.sin(rad) };
  };

  const getLineEndpoints = (angle, innerR, outerR) => {
    const rad = (angle - 90) * Math.PI / 180;
    return {
      x1: center + innerR * Math.cos(rad),
      y1: center + innerR * Math.sin(rad),
      x2: center + outerR * Math.cos(rad),
      y2: center + outerR * Math.sin(rad),
    };
  };

  // Create arc path for text
  const createArcPath = (radius, startDeg, endDeg) => {
    const startRad = (startDeg - 90) * Math.PI / 180;
    const endRad = (endDeg - 90) * Math.PI / 180;
    const x1 = center + radius * Math.cos(startRad);
    const y1 = center + radius * Math.sin(startRad);
    const x2 = center + radius * Math.cos(endRad);
    const y2 = center + radius * Math.sin(endRad);
    const largeArc = (endDeg - startDeg) > 180 ? 1 : 0;
    return `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`;
  };

  const directionRadius = outerRadius * 0.50;
  const iconSize = size * 0.045;

  // Calculate expanded size for torch light overflow
  const expandedSize = size * 2.5;
  const offset = (expandedSize - size) / 2;

  return (
    <svg
      ref={compassRef}
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{ 
        cursor: isLocked ? 'not-allowed' : (isDragging ? 'grabbing' : 'grab'), 
        userSelect: 'none',
        overflow: 'visible',
      }}
      onMouseDown={handleMouseDown}
      data-testid="luo-pan-compass"
    >
      <defs>
        {/* Define circular paths for each direction */}
        {DIRECTIONS.map((dir, i) => {
          const startDeg = dir.degrees - 22.5;
          const endDeg = dir.degrees + 22.5;
          return (
            <path
              key={`path-${dir.short}`}
              id={`dirPath-${dir.short}`}
              d={createArcPath(directionRadius, startDeg, endDeg)}
              fill="none"
            />
          );
        })}
      </defs>

      {/* All rotating compass content wrapped in single rotation group */}
      <g transform={`rotate(${-rotation}, ${center}, ${center})`}>
        
        {/* Compass visuals - can be hidden while keeping torch light */}
        {showCompassOnly && (
          <g style={{ opacity }}>
            {/* Degree markers around the compass - every 15 degrees with major marks at 45° intervals */}
            {Array.from({ length: 72 }, (_, i) => i * 5).map((deg) => {
              const angle = deg;
              const rad = (angle - 90) * Math.PI / 180;
              const isMajor = deg % 45 === 0;
              const isMinor = deg % 15 === 0 && !isMajor;
              const isTiny = deg % 5 === 0 && !isMajor && !isMinor;
              
              const innerR = isMajor ? size * 0.42 : isMinor ? size * 0.43 : size * 0.44;
              const outerR = size * 0.455;
              
              const x1 = center + innerR * Math.cos(rad);
              const y1 = center + innerR * Math.sin(rad);
              const x2 = center + outerR * Math.cos(rad);
              const y2 = center + outerR * Math.sin(rad);
              
              return (
                <line
                  key={`tick-${deg}`}
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke={isMajor ? '#333333' : isMinor ? '#666666' : '#999999'}
                  strokeWidth={isMajor ? 1.5 : isMinor ? 1 : 0.5}
                />
              );
            })}

        {/* Degree numbers - every 10 degrees like a measurement scale */}
        {Array.from({ length: 36 }, (_, i) => i * 10).map((deg) => {
          const angle = deg;
          const normalizedAngle = ((angle % 360) + 360) % 360;
          const rad = (angle - 90) * Math.PI / 180;
          const textR = size * 0.52;
          const x = center + textR * Math.cos(rad);
          const y = center + textR * Math.sin(rad);
          
          const flipText = normalizedAngle > 90 && normalizedAngle < 270;
          const textRotation = flipText ? angle + 180 : angle;
          
          const isCardinal = deg % 90 === 0;
          
          return (
            <text
              key={`deg-${deg}`}
              x={x}
              y={y}
              textAnchor="middle"
              dominantBaseline="central"
              fill={isCardinal ? '#C41E3A' : '#333333'}
              fontSize={isCardinal ? size * 0.026 : size * 0.02}
              fontWeight={isCardinal ? '700' : '500'}
              fontFamily="Arial, sans-serif"
              transform={`rotate(${textRotation}, ${x}, ${y})`}
            >
              {deg}
            </text>
          );
        })}

        {/* Individual mountain divider lines (thin gray) */}
        {MOUNTAINS_DATA.map((mountain, i) => {
          const lineEnd = getLineEndpoints(mountain.degrees - 7.5, outerRadius * 1.02, size * 0.5);
          return (
            <line
              key={`line-${i}`}
              x1={lineEnd.x1}
              y1={lineEnd.y1}
              x2={lineEnd.x2}
              y2={lineEnd.y2}
              stroke="#CCCCCC"
              strokeWidth={0.5}
            />
          );
        })}

        {/* Outer Labels - Element pinyin only (animals are now inside) */}
        {MOUNTAINS_DATA.map((mountain, i) => {
          if (mountain.hasAnimal) return null;
          
          const angle = mountain.degrees;
          const normalizedAngle = ((angle % 360) + 360) % 360;
          const flipText = normalizedAngle > 90 && normalizedAngle < 270;
          const textRotation = flipText ? angle + 180 : angle;
          
          const pinyinPos = getTextPosition(angle, size * 0.46);
          return (
            <g key={`label-${i}`}>
              <text
                x={pinyinPos.x}
                y={pinyinPos.y}
                textAnchor="middle"
                dominantBaseline="central"
                fill="#666666"
                fontSize={size * 0.022}
                fontWeight="500"
                fontFamily="Arial, sans-serif"
                fontStyle="italic"
                transform={`rotate(${textRotation}, ${pinyinPos.x}, ${pinyinPos.y})`}
              >
                {mountain.pinyin}
              </text>
            </g>
          );
        })}
        
        {/* Outermost cream ring */}
        <circle cx={center} cy={center} r={outerRadius} fill="#FFFEF8" stroke="#DDCCBB" strokeWidth="1"/>
        
        {/* Degree tick marks */}
        {Array.from({ length: 360 }, (_, i) => {
          const isMajor = i % 15 === 0;
          const isMedium = i % 5 === 0;
          const line = getLineEndpoints(i, outerRadius * (isMajor ? 0.94 : isMedium ? 0.96 : 0.98), outerRadius);
          return (
            <line
              key={`tick-inner-${i}`}
              x1={line.x1} y1={line.y1} x2={line.x2} y2={line.y2}
              stroke={isMajor ? '#C41E3A' : '#CCBBAA'}
              strokeWidth={isMajor ? 1.2 : 0.4}
            />
          );
        })}

        {/* 24 Mountains Ring */}
        <circle cx={center} cy={center} r={outerRadius * 0.94} fill="#FFFEF8" stroke="#E8D8C8" strokeWidth="0.5"/>
        
        {MOUNTAINS_DATA.map((mountain, i) => {
          const charPos = getTextPosition(mountain.degrees, outerRadius * 0.92);
          const pinyinPos = getTextPosition(mountain.degrees, outerRadius * 0.74);
          const animalIconPos = getTextPosition(mountain.degrees, outerRadius * 0.83);
          const animalIconSize = mountain.zodiac === 'Snake' ? size * 0.05 : size * 0.042;
          
          return (
            <g key={`mountain-${i}`}>
              <path
                d={drawSector(mountain.degrees - 7.5, mountain.degrees + 7.5, outerRadius * 0.68, outerRadius * 0.98)}
                fill={i % 2 === 0 ? '#FFFEF8' : '#FFF5E8'}
                stroke="#E8D8C8"
                strokeWidth="0.4"
              />
              
              {/* Chinese character */}
              <text
                x={charPos.x}
                y={charPos.y}
                textAnchor="middle"
                dominantBaseline="central"
                fill="#1a1a1a"
                fontSize={size * 0.028}
                fontWeight="600"
                fontFamily="'Noto Sans SC', serif"
                transform={`rotate(${mountain.degrees}, ${charPos.x}, ${charPos.y})`}
              >
                {mountain.name}
              </text>
              
              {/* Animal icon for zodiac mountains - show emoji */}
              {mountain.hasAnimal && (
                <text
                  x={animalIconPos.x}
                  y={animalIconPos.y}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontSize={size * 0.032}
                  transform={`rotate(${mountain.degrees}, ${animalIconPos.x}, ${animalIconPos.y})`}
                  style={{ userSelect: 'none' }}
                >
                  {ZODIAC_EMOJI[mountain.zodiac]}
                </text>
              )}
              
              {/* Pinyin label */}
              <text
                x={pinyinPos.x}
                y={pinyinPos.y}
                textAnchor="middle"
                dominantBaseline="central"
                fill="#C41E3A"
                fontSize={size * 0.013}
                fontWeight="500"
                fontFamily="Arial, sans-serif"
                transform={`rotate(${mountain.degrees}, ${pinyinPos.x}, ${pinyinPos.y})`}
              >
                {mountain.pinyin}
              </text>
            </g>
          );
        })}

        {/* Sub-direction ring */}
        <circle cx={center} cy={center} r={outerRadius * 0.72} fill="#FFF8F0" stroke="#E8D8C8" strokeWidth="0.5"/>
        
        {DIRECTIONS.map((dir, dirIdx) => 
          ['1', '2', '3'].map((num, subIdx) => {
            const subAngle = dir.degrees - 22.5 + (subIdx * 15) + 7.5;
            const pos = getTextPosition(subAngle, outerRadius * 0.65);
            return (
              <g key={`sub-${dirIdx}-${subIdx}`}>
                <path
                  d={drawSector(subAngle - 7.5, subAngle + 7.5, outerRadius * 0.58, outerRadius * 0.72)}
                  fill={subIdx === 1 ? '#FFE8C8' : '#FFF8F0'}
                  stroke="#E8D8C8"
                  strokeWidth="0.4"
                />
                <text
                  x={pos.x}
                  y={pos.y}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fill="#8B5A2B"
                  fontSize={size * 0.018}
                  fontFamily="Arial, sans-serif"
                  transform={`rotate(${subAngle}, ${pos.x}, ${pos.y})`}
                >
                  {dir.short}{num}
                </text>
              </g>
            );
          })
        )}

        {/* Direction Ring with curved text */}
        <circle cx={center} cy={center} r={outerRadius * 0.58} fill="#E8A832" stroke="#B8860B" strokeWidth="1.5"/>
        
        {DIRECTIONS.map((dir) => {
          const isCardinal = ['N', 'S', 'E', 'W'].includes(dir.short);
          const startAngle = dir.degrees - 22.5;
          const endAngle = dir.degrees + 22.5;
          const textRadius = outerRadius * 0.50;
          const pathId = `textPath-${dir.short}-${size}`;
          
          // Create path for this specific direction segment
          const arcPath = createArcPath(textRadius, startAngle, endAngle);
          
          return (
            <g key={`dir-${dir.short}`}>
              <path
                d={drawSector(startAngle, endAngle, outerRadius * 0.42, outerRadius * 0.58)}
                fill={isCardinal ? '#C41E3A' : '#E8A832'}
                stroke="#B8860B"
                strokeWidth="0.8"
              />
              {/* Define path for curved text */}
              <defs>
                <path id={pathId} d={arcPath} fill="none"/>
              </defs>
              {/* Curved text along the path */}
              <text
                fill="#FFFFFF"
                fontSize={size * (isCardinal ? 0.022 : 0.015)}
                fontWeight="bold"
                fontFamily="Arial, sans-serif"
              >
                <textPath 
                  href={`#${pathId}`} 
                  startOffset="50%" 
                  textAnchor="middle"
                >
                  {dir.name} {dir.chinese}
                </textPath>
              </text>
            </g>
          );
        })}

        {/* Inner Red Ring */}
        <circle cx={center} cy={center} r={outerRadius * 0.42} fill="#C41E3A" stroke="#8B0000" strokeWidth="1.5"/>
        
        {/* Decorative rings */}
        <circle cx={center} cy={center} r={outerRadius * 0.36} fill="none" stroke="#FFD700" strokeWidth="0.8" strokeDasharray="3,2"/>
        <circle cx={center} cy={center} r={outerRadius * 0.32} fill="#E8A832" stroke="#B8860B" strokeWidth="0.8"/>
        <circle cx={center} cy={center} r={outerRadius * 0.26} fill="#FFFEF8" stroke="#E8D8C8" strokeWidth="0.5"/>

        {/* Center Red Circle */}
        <circle cx={center} cy={center} r={outerRadius * 0.18} fill="#C41E3A" stroke="#8B0000" strokeWidth="2"/>

        {/* Center decorative */}
        <text x={center} y={center} textAnchor="middle" dominantBaseline="central" fill="#FFD700" fontSize={size * 0.055} fontWeight="bold" fontFamily="serif" opacity="0.5">羅</text>
        <line x1={center - outerRadius * 0.1} y1={center} x2={center + outerRadius * 0.1} y2={center} stroke="#FFD700" strokeWidth="1.5"/>
        <line x1={center} y1={center - outerRadius * 0.1} x2={center} y2={center + outerRadius * 0.1} stroke="#FFD700" strokeWidth="1.5"/>
        
        {/* RED lines at major direction group boundaries */}
        {[22.5, 67.5, 112.5, 157.5].map((boundaryAngle, i) => {
          const rad = (boundaryAngle - 90) * Math.PI / 180;
          const extendRadius = size * 0.5;
          const x1 = center + extendRadius * Math.cos(rad);
          const y1 = center + extendRadius * Math.sin(rad);
          const x2 = center - extendRadius * Math.cos(rad);
          const y2 = center - extendRadius * Math.sin(rad);
          return (
            <line
              key={`boundary-line-${i}`}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke="#C41E3A"
              strokeWidth={2.5}
            />
          );
        })}
          </g>
        )}
      </g>

      {/* Kua Direction Analysis Overlay - shows 8 direction sectors colored by element */}
      {kuaAnalysisEnabled && kuaDirections && showCompassOnly && (
        <g transform={`rotate(${-rotation}, ${center}, ${center})`}>
          {Object.entries(kuaDirections).map(([dirKey, dirInfo]) => {
            // Each of 8 directions spans 45 degrees
            const directionAngles = {
              N: 0,
              NE: 45,
              E: 90,
              SE: 135,
              S: 180,
              SW: 225,
              W: 270,
              NW: 315,
            };
            const centerAngle = directionAngles[dirKey];
            const startAngle = centerAngle - 22.5;
            const endAngle = centerAngle + 22.5;
            const startRad = (startAngle - 90) * Math.PI / 180;
            const endRad = (endAngle - 90) * Math.PI / 180;
            const midRad = (centerAngle - 90) * Math.PI / 180;
            
            // Ring from 38% to 48% of compass size
            const innerR = size * 0.38;
            const outerR = size * 0.48;
            const midR = (innerR + outerR) / 2;
            
            // Label position - slightly outside the ring
            const labelR = size * 0.52;
            const labelX = center + labelR * Math.cos(midRad);
            const labelY = center + labelR * Math.sin(midRad);
            
            // Inner label position for element/life aspect
            const innerLabelR = midR;
            const innerLabelX = center + innerLabelR * Math.cos(midRad);
            const innerLabelY = center + innerLabelR * Math.sin(midRad);
            
            const x1 = center + innerR * Math.cos(startRad);
            const y1 = center + innerR * Math.sin(startRad);
            const x2 = center + outerR * Math.cos(startRad);
            const y2 = center + outerR * Math.sin(startRad);
            const x3 = center + outerR * Math.cos(endRad);
            const y3 = center + outerR * Math.sin(endRad);
            const x4 = center + innerR * Math.cos(endRad);
            const y4 = center + innerR * Math.sin(endRad);
            
            const pathD = `M ${x1} ${y1} L ${x2} ${y2} A ${outerR} ${outerR} 0 0 1 ${x3} ${y3} L ${x4} ${y4} A ${innerR} ${innerR} 0 0 0 ${x1} ${y1}`;
            
            // Text rotation - all text follows radial direction (readable from outside)
            const textRotation = centerAngle;
            
            return (
              <g key={`kua-${dirKey}`}>
                {/* Colored sector based on element */}
                <path
                  d={pathD}
                  fill={dirInfo.elementColor}
                  stroke="#ffffff"
                  strokeWidth={2}
                  opacity={0.7}
                />
                {/* Direction label (outer) */}
                <text
                  x={labelX}
                  y={labelY}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill={dirInfo.isAuspicious ? '#059669' : '#dc2626'}
                  fontSize={size * 0.028}
                  fontWeight="700"
                  transform={`rotate(${textRotation}, ${labelX}, ${labelY})`}
                  style={{ textShadow: '0 0 3px white, 0 0 3px white' }}
                >
                  {dirInfo.name}
                </text>
                {/* Element label (first - outer position) */}
                <text
                  x={innerLabelX}
                  y={innerLabelY - size * 0.01}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill="#ffffff"
                  fontSize={size * 0.022}
                  fontWeight="700"
                  transform={`rotate(${textRotation}, ${innerLabelX}, ${innerLabelY})`}
                  style={{ textShadow: '0 0 3px rgba(0,0,0,0.7)' }}
                >
                  {dirInfo.element}
                </text>
                {/* Meaning/Life Aspect label (second - inner position) */}
                <text
                  x={innerLabelX}
                  y={innerLabelY + size * 0.015}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill="#ffffff"
                  fontSize={size * 0.018}
                  fontWeight="500"
                  transform={`rotate(${textRotation}, ${innerLabelX}, ${innerLabelY})`}
                  style={{ textShadow: '0 0 3px rgba(0,0,0,0.7)' }}
                >
                  {dirInfo.lifeAspect}
                </text>
              </g>
            );
          })}
        </g>
      )}

      {/* Torch Light Highlights - OUTSIDE rotation group, rendered separately so it's ALWAYS visible */}
      {highlightedDirections.length > 0 && (
        <g transform={`rotate(${-rotation}, ${center}, ${center})`}>
          {MOUNTAINS_DATA.map((mountain, index) => {
            if (!highlightedDirections.includes(index)) return null;
            
            const startAngle = mountain.degrees - 7.5;
            const endAngle = mountain.degrees + 7.5;
            const startRad = (startAngle - 90) * Math.PI / 180;
            const endRad = (endAngle - 90) * Math.PI / 180;
            const centerRad = (mountain.degrees - 90) * Math.PI / 180;
            
            // Start from center (0) for maximum coverage
            const innerR = 0;
            // Outer radius - extend far beyond compass
            const outerR = size * 2;
            
            const x2 = center + outerR * Math.cos(startRad);
            const y2 = center + outerR * Math.sin(startRad);
            const x3 = center + outerR * Math.cos(endRad);
            const y3 = center + outerR * Math.sin(endRad);
            
            // Position for direction label - inside the beam at about 60% of compass radius
            const labelRadius = outerRadius * 1.3;
            const labelX = center + labelRadius * Math.cos(centerRad);
            const labelY = center + labelRadius * Math.sin(centerRad);
            
            // Unique gradient ID for each torch light
            const gradientId = `torchGradient-${index}`;
            
            // Draw a pie slice from center to outer radius
            const pathD = `M ${center} ${center} L ${x2} ${y2} A ${outerR} ${outerR} 0 0 1 ${x3} ${y3} Z`;
            
            return (
              <g key={`highlight-group-${index}`}>
                <defs>
                  <radialGradient id={gradientId} cx="50%" cy="50%" r="70%" fx="50%" fy="50%">
                    <stop offset="0%" stopColor="#FFD700" stopOpacity="0.9" />
                    <stop offset="20%" stopColor="#FFA500" stopOpacity="0.6" />
                    <stop offset="50%" stopColor="#FF6B00" stopOpacity="0.3" />
                    <stop offset="100%" stopColor="#FF6B00" stopOpacity="0" />
                  </radialGradient>
                </defs>
                <path
                  d={pathD}
                  fill={`url(#${gradientId})`}
                  style={{ mixBlendMode: 'screen' }}
                />
                {/* Direction label inside the torch light beam */}
                <text
                  x={labelX}
                  y={labelY}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fill="#FFFFFF"
                  fontSize={size * 0.035}
                  fontWeight="bold"
                  fontFamily="Arial, sans-serif"
                  style={{
                    textShadow: '0 0 8px #FF6B00, 0 0 16px #FFA500, 2px 2px 4px rgba(0,0,0,0.5)',
                    filter: 'drop-shadow(0 0 4px #FFD700)',
                  }}
                  transform={`rotate(${mountain.degrees}, ${labelX}, ${labelY})`}
                >
                  {mountain.subDir}
                </text>
              </g>
            );
          })}
        </g>
      )}

      {/* Fixed North Pointer - hide with compass, apply opacity */}
      {showCompassOnly && (
        <polygon
          points={`${center},${size * 0.003} ${center - size * 0.01},${size * 0.025} ${center + size * 0.01},${size * 0.025}`}
          fill="#C41E3A"
          stroke="#8B0000"
          strokeWidth="0.5"
          style={{ opacity }}
        />
      )}

      {/* 2026 Flying Star Chart - hide with compass, apply opacity */}
      {showCompassOnly && showFlyingStar && (
        <g transform={`rotate(${-rotation}, ${center}, ${center})`} style={{ opacity }}>
          {/* Flying Star Grid Background */}
          {(() => {
            const baseGridSize = outerRadius * 0.85;
            const gridSize = baseGridSize * (flyingStarScale || 1);
            const cellSize = gridSize / 3;
            const gridX = center - gridSize / 2;
            const gridY = center - gridSize / 2;
            
            return (
              <>
                {/* Main grid background */}
                <rect
                  x={gridX}
                  y={gridY}
                  width={gridSize}
                  height={gridSize}
                  fill="rgba(128, 90, 120, 0.95)"
                  stroke="#8B5A7B"
                  strokeWidth="2"
                  rx="4"
                />
                
                {/* Grid cells */}
                {STAR_GRID.map((row, rowIndex) => 
                  row.map((direction, colIndex) => {
                    const star = FLYING_STARS_2026[direction];
                    const cellX = gridX + colIndex * cellSize;
                    const cellY = gridY + rowIndex * cellSize;
                    const cellCenterX = cellX + cellSize / 2;
                    const cellCenterY = cellY + cellSize / 2;
                    
                    // Determine cell color based on star nature
                    const bgColor = star.isBad 
                      ? 'rgba(100, 60, 80, 0.9)' 
                      : 'rgba(128, 90, 120, 0.7)';
                    
                    return (
                      <g key={`star-${direction}`}>
                        {/* Cell background */}
                        <rect
                          x={cellX + 2}
                          y={cellY + 2}
                          width={cellSize - 4}
                          height={cellSize - 4}
                          fill={bgColor}
                          stroke="#A07090"
                          strokeWidth="1"
                          rx="2"
                        />
                        
                        {/* Star number - large */}
                        <text
                          x={cellCenterX}
                          y={cellCenterY - cellSize * 0.12}
                          textAnchor="middle"
                          dominantBaseline="central"
                          fill="#FFFFFF"
                          fontSize={cellSize * 0.38}
                          fontWeight="bold"
                          fontFamily="Georgia, serif"
                        >
                          {star.number}
                        </text>
                        
                        {/* Star Chinese name - top left */}
                        <text
                          x={cellX + 6}
                          y={cellY + cellSize * 0.18}
                          textAnchor="start"
                          fill="#D4AF37"
                          fontSize={cellSize * 0.11}
                          fontWeight="bold"
                          fontFamily="'Noto Sans SC', serif"
                        >
                          {star.star.slice(0, 2)}
                        </text>
                        <text
                          x={cellX + 6}
                          y={cellY + cellSize * 0.30}
                          textAnchor="start"
                          fill="#D4AF37"
                          fontSize={cellSize * 0.11}
                          fontWeight="bold"
                          fontFamily="'Noto Sans SC', serif"
                        >
                          {star.star.slice(2)}
                        </text>
                        
                        {/* Star English name - below number */}
                        <text
                          x={cellCenterX}
                          y={cellCenterY + cellSize * 0.22}
                          textAnchor="middle"
                          fill="#E8D8C8"
                          fontSize={cellSize * 0.09}
                          fontWeight="bold"
                          fontFamily="Arial, sans-serif"
                        >
                          {star.pinyin} Star
                        </text>
                        
                        {/* English meaning - bottom */}
                        <text
                          x={cellCenterX}
                          y={cellCenterY + cellSize * 0.36}
                          textAnchor="middle"
                          fill="#E8D8C8"
                          fontSize={cellSize * 0.075}
                          fontStyle="italic"
                          fontFamily="Arial, sans-serif"
                        >
                          {star.meaning}
                        </text>
                        
                        {/* Direction label (for non-center cells) */}
                        {direction !== 'CENTER' && (
                          <text
                            x={cellX + cellSize - 6}
                            y={cellY + cellSize * 0.18}
                            textAnchor="end"
                            fill="rgba(255,255,255,0.5)"
                            fontSize={cellSize * 0.1}
                            fontFamily="Arial, sans-serif"
                          >
                            {direction}
                          </text>
                        )}
                      </g>
                    );
                  })
                )}
                
                {/* North label at top */}
                <text
                  x={center}
                  y={gridY - gridSize * 0.025}
                  textAnchor="middle"
                  fill="#E8D8C8"
                  fontSize={gridSize * 0.055}
                  fontFamily="Arial, sans-serif"
                >
                  North 北
                </text>
                
                {/* South label at bottom */}
                <text
                  x={center}
                  y={gridY + gridSize + gridSize * 0.045}
                  textAnchor="middle"
                  fill="#E8D8C8"
                  fontSize={gridSize * 0.055}
                  fontFamily="Arial, sans-serif"
                >
                  South 南
                </text>
              </>
            );
          })()}
        </g>
      )}
    </svg>
  );
};

export default LuoPanCompass;
